import "./App.css";
import { useEffect, useState, lazy, Suspense, useRef } from "react";
import Loader from "./Loader";
import Login from "./loginscreen/Login";
import {
  HubConnectionBuilder,
  LogLevel,
  HttpTransportType,
  HubConnectionState
} from "@microsoft/signalr";

import { useAuth } from "./utils/AuthProvider";
import StringUtils from "./utils/StringUtils";
import { Routes, Route, Navigate } from "react-router-dom";
import Unauthorized from "./Unauthorized/Unauthorized";
import ProtectedRoute from "./utils/ProtectedRoute";


const Nurse = lazy(() => import("./nursescreen/Nurse"));
const Doctor = lazy(() => import("./doctorscreen/Doctor"));
const Front = lazy(() => import("./frontscreen/Front"));
const Admin = lazy(() => import("./adminscreen/Admin"));
const SuperAdmin = lazy(() => import("./superadminscreen/SuperAdmin"));

//renderLoader component calls Loader component
// It renders a loader component used during the lazy loading process.
const renderLoader = () => (
  <div
    style={{
      width: "100vw",
      height: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ width: "100px", height: "100px" }}>
        <Loader color="#000" />
      </div>
    </div>
  </div>
);

function AppRoutes() {
  //The appstate represents the current screen , It renders different screens based on current value of appstate
  
  const { appstate, updateAppState, appStateRef, token, credentialid, currentClinic, currentRole, user, reloadRoute } = useAuth();

  // mainHub (role-level) ──
  const [hubconnection, sethubconnection] = useState(null);
  const [connectionState, setconnectionState] = useState('disconnected');

  // appointmentHub (clinic-level) ──
  const [appointmentHubConnection, setAppointmentHubConnection] = useState(null);
  const [appointmentConnectionState, setAppointmentConnectionState] = useState('disconnected');
  //When using the above state variable Appstate, it gets set Asynchronously but we need a variable which
  //gets set instantly or synchronously so that it can be used as a flag to not restart the hubconnection
  //once we logout.


  //useEffect is used to initialize mainHub connection
  useEffect(() => {
    //console.log("entered useeffect for initializing connection inside app.js");
    // Clear hub when logged out (no token or role)
    if (!token || !currentRole) {
      sethubconnection(null);
      setconnectionState('disconnected');
      return;
    }

    // console.log("entered useeffect for initializing connection and now starting initialization in app.js");
    //new hub-connection is created when 'token' or 'currentRole' changes (rebuild on role switch)
    const connection = new HubConnectionBuilder()
      .withUrl(
        `${process.env.REACT_APP_HUB_ENDPOINT}/mainHub?roleId=${currentRole.id}&roletype=${currentRole.role}`,
        {
          accessTokenFactory: () => token,
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
        }
      )
      // Q? Didn't get this below line properly ?
      .configureLogging(LogLevel.Trace) //connection will log information messages
      .build();

    sethubconnection(connection);

    // Stop previous connection when token/role changes or component unmounts
    return () => {
      connection.stop();
    };
  }, [token, currentRole?.id, currentRole?.role]);



  const [selectedDoctorId, setSelectedDoctorId] = useState(() => {
     return localStorage.getItem("selectedDoctor") || null;
  });

  useEffect(() => {
    const handleDoctorChange = (e) => {
      setSelectedDoctorId(e.detail);
    };
    window.addEventListener('selectedDoctorChanged', handleDoctorChange);
    return () => window.removeEventListener('selectedDoctorChanged', handleDoctorChange);
  }, []);

  //  Build appointmentHub when a clinic and doctor are selected ──
  useEffect(() => {
    const doctorParam = currentClinic?.role === "Doctor" ? currentRole?.id : selectedDoctorId;

    if (token && currentClinic && doctorParam) {
      // console.log("[appointmentHub] Building connection for clinicId and doctor param:", currentClinic.id, doctorParam);
      const connection = new HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_HUB_ENDPOINT}/appointmentHub?clinicId=${currentClinic.id}&doctorId=${doctorParam}`, {
          accessTokenFactory: () => token,

        })
        .configureLogging(LogLevel.Information)
        .build();
      setAppointmentHubConnection(connection);
      // console.log("[appointmentHub] Connection object created.");
    } else {
      setAppointmentHubConnection(null);
    }
  }, [token, currentClinic, currentRole?.id, selectedDoctorId]);

  // ── Start appointmentHub once the connection object exists ──
  useEffect(() => {
    if (!appointmentHubConnection) return;

    async function startAppointmentHub() {
      try {
        // console.log("[appointmentHub] Attempting to connect...");
        setAppointmentConnectionState('connecting');
        if (
          appointmentHubConnection.state === HubConnectionState.Connected ||
          appointmentHubConnection.state === HubConnectionState.Connecting
        ) {
          setAppointmentConnectionState(
            appointmentHubConnection.state === HubConnectionState.Connected ? 'connected' : 'connecting'
          );
          return;
        }
        await appointmentHubConnection.start();
        setAppointmentConnectionState('connected');
        console.log("[appointmentHub] Connected! State:", appointmentHubConnection.state);
      } catch (err) {
        console.error("[appointmentHub] Connection error:", err);
        setTimeout(startAppointmentHub, 5000);
      }
    }

    appointmentHubConnection.onclose(async (error) => {
      console.log("[appointmentHub] Connection closed. Attempting to reconnect...");
      setAppointmentConnectionState('disconnected');
      // Reconnect only while user is still logged in (token present)
      if (token) {
        if (
          appointmentHubConnection.state === HubConnectionState.Connected ||
          appointmentHubConnection.state === HubConnectionState.Connecting
        ) {
          return;
        }
        setTimeout(startAppointmentHub, 5000);
      }
    });

    if (appointmentHubConnection.state === HubConnectionState.Disconnected) {
      startAppointmentHub();
    }

    return () => {
      if (
        appointmentHubConnection.state === HubConnectionState.Connected ||
        appointmentHubConnection.state === HubConnectionState.Connecting
      ) {
        console.log("[appointmentHub] Stopping on unmount.");
        appointmentHubConnection.stop();
        setAppointmentConnectionState('disconnected');
      }
    };
  }, [appointmentHubConnection]);

  // Stop appointmentHub on logout 
  useEffect(() => {
    if (!token && appointmentHubConnection) {
      console.log("[appointmentHub] Logout detected — stopping connection.");
      appointmentHubConnection.stop();
      setAppointmentHubConnection(null);
      setAppointmentConnectionState('disconnected');
    }
  }, [token]);


  //useEffect is used to manage hub connection
  useEffect(() => {
    //console.log("entered useeffect for starting connection inside app.js");
    appStateRef.current = appstate;

    //appstate == "initial" && token == "" is by default
    // Start when logged in: token + role + hub object (was appstate !== "initial", which never became true)
    if (!token || !currentRole || !hubconnection) {
      return;
    }

    //start() async function is used start the connection and handles errors.
    //console.log("entered useeffect for starting connection and now starting connection in app.js");
    async function start() {
      //console.log("entered start function");
      try {
        setconnectionState('connecting');
        if (
          hubconnection.state === HubConnectionState.Connected ||
          hubconnection.state === HubConnectionState.Connecting
        ) {
          //console.log("hubconnection already connected in start function")
          setconnectionState(
            hubconnection.state === HubConnectionState.Connected ? 'connected' : 'connecting'
          );
          return;
        }
        await hubconnection.start();
        //console.log("SignalR Connected.");
        //sethubconnection(connection);
        setconnectionState('connected');
        localStorage.setItem("connectionStatus", 1);
      } catch (err) {
        console.log(err);
        //start function runs again after 5s when error accurs
        setTimeout(start, 5000);
      }
    }

    const handleReceiveMessage = (user, message) => {
      ////console.log("broadcasting is ", broadcasting)
      if (StringUtils.contains(message, "TIMEUPDATE")) {
        let arr = message.split("_");
        let time = arr[1];
        ////console.log("setting my time",time);
        localStorage.setItem('currentTime', parseInt(time));
        // setCurrentTime(parseInt(time));
      }
    };

    //this callback function is used when hub connection is closed
    const handleClose = async (error) => {
      //Do not restart the hubconnection if logged out.
      /*
      Reconnect while token is present (user still logged in).
      Nurse/front TopBar notifications require an active mainHub connection.
      */
      //console.log("onclose callbck being called")
      //console.log("connection closed");
      setconnectionState('disconnected');
      localStorage.setItem("connectionStatus", 0);
      if (!token) return;
      //need to give timeout for server to end previous connection before starting new one
      if (
        hubconnection.state === HubConnectionState.Connected ||
        hubconnection.state === HubConnectionState.Connecting
      ) {
        //console.log("hubconnection already connected or connecting in onclose")
        //setconnectionState('connected')
        return;
      }
      setTimeout(start, 5000);
    };

    hubconnection.onclose(handleClose);
    hubconnection.on("ReceiveMessage", handleReceiveMessage);

    if (hubconnection.state === HubConnectionState.Disconnected) {
      // Start the connection.
      //console.log("starting signalr fron appjsx",hubconnection.state)
      if (hubconnection.state === HubConnectionState.Connected || hubconnection.state === HubConnectionState.Connecting) {
        //console.log("hubconnection already connected or connecting in main useeffect")
        return;
      }
      start();
    }

    return () => {
      hubconnection.off("ReceiveMessage", handleReceiveMessage);
    };
  }, [token, currentRole, hubconnection, appstate]); //useEffect runs when token, role, or hubconnection change



 
  /*
  apphandler, tokenhandler, credentialidhandler are Handler Functions and these functions are 
  used to handle state changes.
  The handler functions are not directly called within code. Instead, these handler functions are 
  passed down as props to child components where they can be called and used.

  apphandler: It is passed as a prop to the Login, Doctor, Nurse, Pdh, Front, Admin, and SuperAdmin 
    components. These components can call this function and change appstate 

  tokenhandler: Similar to apphandler, it is passed as a prop to the different components. 
      When the user logs in and the authentication token is received, 
      the components can call this function to set the token in the App component.

  credentialidhandler: Like the other handlers, it is passed as a prop to the different components. 
        When the user provides their credentials, the components can call this function to 
        set the credentialid in the App component.
  
  */

  return (

    <div className="App">
      <Routes>

        {/*<Route path="/" element={<Navigate to="/login" replace />} />*/}

        <Route path="/*" element={
          currentClinic && currentRole ? <><Navigate to={`clinics/${currentClinic.id}/roles/${currentRole.id}/${currentRole.role.toLowerCase()}`} replace /></> :
            <Suspense fallback={renderLoader()}>
              {<Login />}
            </Suspense>} />
        <Route element={<ProtectedRoute allowedRoles={['Doctor']} clinic={currentClinic} />}>
          <Route path="/clinics/:clinicId/roles/:roleId/doctor/*" element={<Suspense fallback={renderLoader()}>
            <Doctor
              apphandler={updateAppState}
              hubconnection={hubconnection}
              sethubconnection={sethubconnection}
              connectionState={connectionState}
              appointmentHubConnection={appointmentHubConnection}
              reloadRoute={reloadRoute}
            />
          </Suspense>} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={['Nurse']} clinic={currentClinic} />}>
          <Route path="/clinics/:clinicId/roles/:roleId/nurse/*" element={<Suspense fallback={renderLoader()}>
            <Nurse
              apphandler={updateAppState}
              hubconnection={hubconnection}
              sethubconnection={sethubconnection}
              connectionState={connectionState}
             appointmentHubConnection={appointmentHubConnection}
              reloadRoute={reloadRoute}
            />
          </Suspense>} />
        </Route>

        <Route
          element={<ProtectedRoute allowedRoles={['front']} clinic={currentClinic} />}
        >
          <Route path="/clinics/:clinicId/roles/:roleId/front/*" element={<Suspense fallback={renderLoader()}>
            <Front
              apphandler={updateAppState}
              hubconnection={hubconnection}
              sethubconnection={sethubconnection}
              connectionState={connectionState}
              appointmentHubConnection={appointmentHubConnection}
              reloadRoute={reloadRoute}
            />
          </Suspense>} />
        </Route>

        <Route
          element={<ProtectedRoute allowedRoles={['admin']} clinic={currentClinic} />}
        >
          <Route path="/clinics/:clinicId/roles/:roleId/admin/*" element={<Suspense fallback={renderLoader()}>
            <Admin
              apphandler={updateAppState}
              token={token}
              credentialid={credentialid}
              hubconnection={hubconnection}
              connectionState={connectionState}
              sethubconnection={sethubconnection}
              reloadRoute={reloadRoute}
            />
          </Suspense>} />
        </Route>
        <Route path="/super_admin/*" element={
          (localStorage.getItem("usergroup") === "Super_Admin") ?
            <Suspense fallback={renderLoader()}>
              <SuperAdmin
                apphandler={updateAppState}
                token={token}
                credentialid={credentialid}
                hubconnection={hubconnection}
                connectionState={connectionState}
                sethubconnection={sethubconnection}
              />
            </Suspense> : <Navigate to="/" replace />} />

        <Route path="/unauthorized" element={<Unauthorized />} />

      </Routes>

    </div>
    /*
      ThemeProvider: The entire app is wrapped inside ThemeProvider to provide the Material-UI
       theme to all components.
    */
  );
}


export default AppRoutes;
